// One app for every screen. Anyone holding an old /m link comes back to the single workspace.
if (location.pathname.startsWith("/m")) { localStorage.removeItem("timo-force-desktop"); location.replace("/app" + location.hash); }
// The landing page at / reads this to send a returning person straight to their workspace.
const SIGNED_IN = "timo-signed-in";

/** Timo web, boot sequence: shell → auth gate → workspace subscriptions → render loop. */
import "./styles/app.css";
import { addDays, startOfDay } from "./lib/time";
import { byId, on, toast } from "./lib/dom";
import { friendlyAuthError } from "./lib/authmessage";
import { completeRedirectSignIn, devSignIn, onUser, signInWithApple, signInWithGoogle, signOut, ensureWorkspace } from "./data/auth";
import { usingEmulators } from "./lib/firebase";
import { savePreferences, watchBookingPage, watchEvents, watchFocusSessions, watchMeetings, watchNotes, watchPreferences, watchResearch, watchTasks, watchSetupFlags } from "./data/store";
import { recoverPendingUploads } from "./capture/recorder";
import { state, patch, subscribe } from "./state";
import { setLoader, setProfile, setupShell, onPageChange, currentPage } from "./ui/shell";
import { createOrb, type Orb } from "./ui/orb";
import { renderToday, setupTodayNav } from "./ui/today";
import { renderCalendar, setupCalendar } from "./ui/calendar";
import { setupEvents } from "./ui/events";
import { renderTasks, setupTasks } from "./ui/tasks";
import { renderMeetings, setupMeetings } from "./ui/meetings";
import { bindLatestIfIdle, refreshAssistant, setupMeeting, setupShare, startMeeting } from "./ui/meeting";
import { setupCreate } from "./ui/create";
import { refreshPushToken } from "./lib/push";
import { connectCalendar, handleCalendarReturn, runCalendarSync, setConnectionsUser, setupOnboarding, showOnboarding } from "./ui/onboarding";
import { hasActiveConnections, renderConnections, watchConnections } from "./ui/connections";
import { renderNotes, setupNotes } from "./ui/notes";
import { renderResearch, setupResearch } from "./ui/research";
import { renderInbox, setupInbox } from "./ui/inbox";
import { renderSettings } from "./ui/settings";
import { renderFocus, setupFocus } from "./ui/focus";
import { renderAnalytics, setupAnalytics } from "./ui/analytics";
import { renderBooking } from "./ui/booking";
import { setupExtension } from "./ui/extension";
import { setupCommand } from "./ui/command";
import { checkAdmin, renderAdmin, userIsAdmin } from "./ui/admin";
import { setupHelp } from "./ui/help";
import { trackOpens } from "./lib/activity";
import { sendAcquisition } from "./lib/acquisition";

let unsubs: (() => void)[] = [];
let booted = false;

function renderAll() {
  renderToday(state.events, state.tasks, state.userName);
  renderCalendar(state.events, state.prefs.workStart, state.prefs.workEnd);
  renderTasks(state.tasks);
  renderMeetings(state.meetings);
  refreshAssistant();
  renderNotes(state.notes);
  renderResearch(state.research);
  renderInbox(state.tasks, state.research);
  renderSettings(state.prefs, { name: state.userName, email: state.userEmail, tz: state.prefs.timezone });
  renderFocus(state.tasks, state.events, state.focusSessions);
  renderAnalytics(state.tasks, state.events, state.meetings, state.focusSessions);
  if (currentPage() === "booking") renderBooking(state.bookingPage, state.userName, state.prefs);
  $$sync();
}
function $$sync() {
  (byId("autoNotesToggle") as HTMLInputElement | null) && ((byId("autoNotesToggle") as HTMLInputElement).checked = state.prefs.autoNotes);
  (byId("autoTasksToggle") as HTMLInputElement | null) && ((byId("autoTasksToggle") as HTMLInputElement).checked = state.prefs.autoTasks);
}

let signinOrb: Orb | null = null;
let heroOrb: Orb | null = null;
function showSignin(show: boolean, error = "") {
  const el = byId("signin")!;
  el.classList.toggle("show", show);
  el.setAttribute("aria-hidden", String(!show));
  byId("signinError")!.innerHTML = error ? friendlyAuthError(error).html : "";
  const btn = byId<HTMLButtonElement>("signinGoogle");
  if (btn) { btn.disabled = false; btn.querySelector("span")!.textContent = "Continue with Google"; }
  // The orb is the other door: it lives only while the signed-out page is up.
  const host = byId("signinOrbHost");
  if (show && host && !signinOrb) { signinOrb = createOrb(host, 76); signinOrb.setState("listening"); }
  if (!show && signinOrb) { signinOrb.destroy(); signinOrb = null; }
}

/** Both the orb and the button open the same Google flow. */
async function startGoogleSignIn() {
  showSignin(true, "");
  const btn = byId<HTMLButtonElement>("signinGoogle")!;
  if (btn.disabled) return;
  btn.disabled = true; btn.querySelector("span")!.textContent = "Opening Google…";
  signinOrb?.setState("processing");
  // Basic profile only: no sensitive scopes at sign-in. Calendar access is requested later, when the
  // person chooses to connect it (incremental authorization keeps the consent screen clean).
  try { await signInWithGoogle(false); } catch (err) { showSignin(true, (err as Error).message); signinOrb?.setState("listening"); }
}

function bootUI() {
  if (booted) return;
  booted = true;
  setupShell();
  setupTodayNav(renderAll);
  setupTasks(() => state.tasks);
  setupMeeting();
  setupCreate();
  setupShare();
  setupMeetings(() => state.meetings);
  setupOnboarding(() => state.prefs);
  setupNotes(() => state.notes);
  setupResearch();
  setupInbox(() => state.tasks);
  setupFocus(() => state.tasks);
  setupAnalytics(renderAll);
  setupExtension();
  setupCommand();
  setupHelp();
  document.addEventListener("timo:rerender", renderAll);
  onPageChange((p) => p === "booking" && renderBooking(state.bookingPage, state.userName, state.prefs));
  onPageChange((p) => p === "admin" && void renderAdmin());
  onPageChange((p) => { if (p === "settings") void import("./ui/billing").then((m) => m.renderBilling()); });
  onPageChange((p) => { if (p === "integrations") { const h = byId("aiConnectionsHost"); if (h) renderConnections(h, state.userName); } });
  on(byId("intNewConnection"), "click", () => byId("aiConnectionsPanel")?.scrollIntoView({ behavior: "smooth" }));
  // Delegated on purpose. Settings rebuilds its own markup, so a listener bound to an element there
  // is thrown away on the next render and the button silently stops working.
  document.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!el) return;
    if (el.dataset.action === "pair-phone") void import("./ui/pairphone").then((m) => m.showPairPhone());
    if (el.dataset.action === "upload-recording") void import("./ui/upload").then((m) => m.pickRecording());
  });
  subscribe(renderAll);
  setupCalendar(renderAll);
  setupEvents();
  onPageChange((p) => p === "calendar" && renderCalendar(state.events, state.prefs.workStart, state.prefs.workEnd));
  document.addEventListener("timo:connect-calendar", () => void connectCalendar());
  document.addEventListener("timo:pref", ((e: CustomEvent<Record<string, boolean>>) => void savePreferences(e.detail)) as EventListener);
  if (usingEmulators) {
    byId("signinGoogle")!.insertAdjacentHTML("afterend", `<button class="signin-google" id="signinDev" type="button" style="margin-top:10px;border-style:dashed"><span>Dev sign-in (emulator)</span></button>`);
    on(byId("signinDev"), "click", async () => { try { await devSignIn(); } catch (err) { showSignin(true, (err as Error).message); } });
  }
  on(byId("signinGoogle"), "click", () => void startGoogleSignIn());
  on(byId("signinApple"), "click", async () => {
    showSignin(true, "");
    const btn = byId<HTMLButtonElement>("signinApple")!;
    if (btn.disabled) return;
    btn.disabled = true; btn.querySelector("span")!.textContent = "Opening Apple…";
    signinOrb?.setState("processing");
    try { await signInWithApple(); } catch (err) {
      btn.disabled = false; btn.querySelector("span")!.textContent = "Continue with Apple";
      showSignin(true, (err as Error).message); signinOrb?.setState("listening");
    }
  });
  on(byId("signinOrb"), "click", () => void startGoogleSignIn());
  // The day page's own record control. Both the orb and the button do the same thing, because the
  // orb is what people recognise from the phone and the button is what people read.
  const heroHost = byId("dayRecordOrbHost");
  if (heroHost && !heroOrb) { heroOrb = createOrb(heroHost, 76); heroOrb.setState("listening"); }
  on(byId("dayRecordOrb"), "click", () => void startMeeting());
  on(byId("dayRecordStart"), "click", () => void startMeeting());
  on(byId("dayRecordCall"), "click", () => void startMeeting(undefined, "call"));
  // Sign out from the profile block
  const profile = document.querySelector<HTMLElement>(".rail .profile");
  profile?.insertAdjacentHTML("afterend", `<button id="signOutBtn" type="button">Sign out</button>`);
  on(byId("signOutBtn"), "click", () => void signOut());
  on(byId("syncCalendarBtn"), "click", () => void runCalendarSync());
}

const redirectError = await completeRedirectSignIn();
onUser(async (user) => {
  bootUI();
  if (redirectError) showSignin(true, redirectError.replace("Firebase: ", ""));
  unsubs.forEach((u) => u());
  unsubs = [];
  if (!user) {
    try { localStorage.removeItem(SIGNED_IN); } catch { /* private mode */ }
    patch({ tasks: [], events: [], meetings: [], notes: [], research: [], focusSessions: [], bookingPage: null, activeMeetingId: null });
    setLoader("Sign in to continue", true);
    showSignin(true);
    // Arriving from the landing page's Google or Apple button: carry on with that sign-in rather
    // than asking again. The click happened on the previous page, so this has to be a redirect.
    const wanted = new URLSearchParams(location.search).get("signin");
    if ((wanted === "google" || wanted === "apple") && !redirectError) {
      const q = new URLSearchParams(location.search); q.delete("signin");
      history.replaceState(null, "", `${location.pathname}${q.size ? `?${q}` : ""}${location.hash}`);
      const btn = byId<HTMLButtonElement>(wanted === "apple" ? "signinApple" : "signinGoogle");
      if (btn) { btn.disabled = true; btn.querySelector("span")!.textContent = wanted === "apple" ? "Opening Apple…" : "Opening Google…"; }
      signinOrb?.setState("processing");
      void (wanted === "apple" ? signInWithApple(true) : signInWithGoogle(false, true)).catch((err) => showSignin(true, (err as Error).message));
    }
    return;
  }
  showSignin(false);
  try { localStorage.setItem(SIGNED_IN, "1"); } catch { /* private mode */ }
  setLoader("Opening your workspace…");
  await ensureWorkspace(user);
  const name = (user.displayName ?? user.email?.split("@")[0] ?? "there").split(" ")[0]!;
  patch({ userName: name, userEmail: user.email ?? "" });
  setConnectionsUser(name);
  watchConnections(() => { const banner = byId("agentConnectBanner"); if (banner && hasActiveConnections()) banner.classList.add("hidden"); const h = byId("aiConnectionsHost"); if (h && currentPage() === "integrations") renderConnections(h, name); const o = byId("aiConnectSetup"); if (o && byId("onboarding")?.classList.contains("show")) renderConnections(o, name, true); });
  setProfile(user.displayName ?? user.email ?? "You");
  void refreshPushToken();
  trackOpens();
  sendAcquisition();

  const from = addDays(startOfDay(new Date()), -14);
  const to = addDays(startOfDay(new Date()), 28);
  let first = true;
  // The loader stays up until every live collection has painted once, so the prototype markup never flashes.
  const arrived = new Set<string>();
  const NEEDED = ["prefs", "tasks", "events", "meetings"];
  let loaderDone = false;
  const finishLoader = () => { if (loaderDone) return; loaderDone = true; renderAll(); setLoader("Ready", true); };
  const landed = (k: string) => { if (!state.loaded[k as keyof typeof state.loaded]) patch({ loaded: { ...state.loaded, [k]: true } }); arrived.add(k); if (NEEDED.every((n) => arrived.has(n))) finishLoader(); };
  window.setTimeout(finishLoader, 8000); // offline or a slow network: show what we have
  unsubs.push(
    watchPreferences((prefs) => {
      patch({ prefs });
      // Self-heal: the browser knows the real timezone; agents and the scheduler rely on it.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && prefs.timezone !== tz && !prefs.timezoneLocked) void savePreferences({ timezone: tz });
      if (first) {
        first = false;
        setLoader("Syncing meetings and focus blocks…");
        landed("prefs");
        if (handleCalendarReturn(prefs)) { /* onboarding resumed */ }
        else if (!prefs.onboarded) showOnboarding(prefs, 0);
        else if (prefs.calendarConnected) void runCalendarSync(true);
      }
    }),
    watchTasks((tasks) => { patch({ tasks }); landed("tasks"); }),
    watchEvents(from, to, (events) => { patch({ events }); landed("events"); }),
    watchMeetings((meetings) => { patch({ meetings }); bindLatestIfIdle(meetings); landed("meetings"); }),
    watchNotes((notes) => patch({ notes })),
    watchResearch((research) => patch({ research })),
    watchFocusSessions((focusSessions) => patch({ focusSessions })),
    watchBookingPage((bookingPage) => patch({ bookingPage })),
    watchSetupFlags((setup) => patch({ setup })),
  );
  void checkAdmin().then((a) => { if (a && currentPage() === "admin") void renderAdmin(); });
  const recovered = await recoverPendingUploads().catch(() => 0);
  if (recovered) toast(`Resuming ${recovered} unsent recording segment${recovered > 1 ? "s" : ""}`);
  if (!location.hash) history.replaceState(null, "", `#${currentPage()}`);
  // Returning from Stripe: the webhook has usually landed by now, so read the plan again.
  if (new URLSearchParams(location.search).has("billing")) {
    const paid = new URLSearchParams(location.search).get("billing") === "done";
    void import("./ui/billing").then((m) => m.renderBilling());
    if (paid) toast("Thanks. Your plan is active");
    history.replaceState(null, "", `/app#settings`);
  }
});
